import { JSX } from "react";
import { DefaultLegendContentProps, TooltipContentProps } from "recharts";
import { Payload, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { EcdColorsContextValues } from "@app/overviews/enterprise-compliance-dashboard/contexts/EcdColorsContext";
import GapByAgeTooltipTable from "@app/overviews/enterprise-compliance-dashboard/GapByAgeTooltipTable";
import { EcdAxisTickProps, EcdTrendingArrowEnum, TimeFrameOptions } from "@app/overviews/enterprise-compliance-dashboard/interfaces";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesColors } from "@app/shared/qes-standards/colors";

export const getRandomValue = (rangeStart: number, rangeEnd: number, decimalPlaces: number): string => {
  if (decimalPlaces > 0) {
    return Math.max(rangeStart, Math.random() * rangeEnd).toFixed(decimalPlaces).toLocaleString();
  }
  return Math.max(rangeStart, Math.floor(Math.random() * rangeEnd)).toLocaleString();
}

export const CustomXAxisTickOffsetX = (props: EcdAxisTickProps): JSX.Element => {
  const { x, y, payload, xOffset } = props;
  const arbitraryRenderLengthLimit = 15;
  let renderedValue = payload.value;
  if (renderedValue.length > arbitraryRenderLengthLimit) {
    renderedValue = `${renderedValue.substring(0, arbitraryRenderLengthLimit - 3)}...`;
  }
  return (
    <g transform={`translate(${x - (xOffset || 32)},${y + 15}) rotate(-24)`}>
      <text x={0} y={0}
        textAnchor="middle"
        fill={QesColors.QesPrimaryGray7}>{renderedValue}</text>
    </g>
  );
}

export const getYAxisWidth = (yAxisValues: number[]): number => {
  const defaultWidth = 35;
  if (!yAxisValues || yAxisValues.length === 0) {
    return defaultWidth;
  }
  const maxValue = Math.max(...yAxisValues);
  const digitsArray = maxValue.toString().match(/\d/g);
  // 10px per number and 4px per comma
  return Math.max(defaultWidth, (digitsArray.length * 10) + (Math.floor(digitsArray.length / 3) * 4));
}

export const gapOverviewCustomTooltip = (props: TooltipContentProps<ValueType, string | number>): JSX.Element => {
  if (props && props.payload && props.payload.length > 0) {
    const payloadItem = props.payload[0];
    const payloadItems = [
      getCustomTooltipLineItem(payloadItem.name as string, payloadItem.color, payloadItem.value as string),
    ];
    const tooltipTitle = payloadItem.payload.week && payloadItem.payload.weekEnd ?
      `${payloadItem.payload.week} - ${payloadItem.payload.weekEnd}` :
      undefined;
    return getCustomTooltipBody(tooltipTitle, payloadItems);
  }
}

export const gapsByAgeCustomTooltip = (props: TooltipContentProps<ValueType, string | number>, xAxisKey: string, orderParam?: number[]): JSX.Element => {
  if (props && props.payload && props.payload.length > 0) {
    /* Recharts renders bars bottom to top in the order declared, while rendering the tooltip
        top to bottom in the order declared, so we reverse them here unless a specific order was provided
    */
    const order = orderParam || Array.from(Array(props.payload.length)).map((_, index) => index).reverse();
    const tooltipItems = order.map((index) => {
      return props.payload[index];
    });
    return getGapsByAgeCustomTooltipBody(props.payload[0].payload[xAxisKey], tooltipItems);
  }
}

export const customTooltip = (props: TooltipContentProps<ValueType, string | number>, xAxisKey: string, orderParam?: number[]): JSX.Element => {
  /**
   * Each stacked bar has its own entry, so props.payload[0]...props.payload[n]
   * Each one's payload includes values for every bar on the stack,
   * but we need to iterate over all of them to get the colors
   */
  if (props && props.payload && props.payload.length > 0) {
    /* Recharts renders bars bottom to top in the order declared, while rendering the tooltip
        top to bottom in the order declared, so we reverse them here unless a specific order was provided
    */
    const order = orderParam || Array.from(Array(props.payload.length)).map((_, index) => index).reverse();
    const tooltipItems: JSX.Element[] = order.map((index) => {
      const payloadItem = props.payload[index];
      return getCustomTooltipLineItem(payloadItem.name as string, payloadItem.color, payloadItem.value as string);
    });
    return getCustomTooltipBody(props.payload[0].payload[xAxisKey], tooltipItems);
  }
}

const getCustomTooltipBody = (tooltipTitle: string, tooltipItems: JSX.Element[]) => {
  return (
    <div className="qes-dashboard__custom-tooltip">
      {tooltipTitle ? <p className="qes-dashboard__tooltip-title">{tooltipTitle}</p> : null}
      {tooltipItems}
    </div>
  );
}

const getGapsByAgeCustomTooltipBody = (tooltipTitle: string, tooltipItems: Payload<ValueType, string | number>[]) => {
  return (
    <div className="qes-dashboard__custom-tooltip">
      {tooltipTitle ? <p className="qes-dashboard__tooltip-title">{tooltipTitle}</p> : null}
      <GapByAgeTooltipTable
        payload={ tooltipItems }
      />
    </div>
  );
}

const getCustomTooltipLineItem = (name: string, fillColor: string, value: string) => {
  return (
    <p key={name}>
      <i className="qes-dashboard__color-rect" style={{ backgroundColor: fillColor }} />
      <span>{name}:</span> {value.toLocaleString()}
    </p>
  );
}

export const customLegend = (props: DefaultLegendContentProps, orderParam?: number[]): JSX.Element[] => {
  /* Recharts renders bars bottom to top in the order declared, while rendering the legend
      top to bottom in the order declared, so we reverse them here unless a specific order was provided
  */
  const order = orderParam || Array.from(Array(props.payload.length)).map((_, index) => index).reverse();
  const legendItems: JSX.Element[] = order.map((index) => {
    const payloadItem = props.payload[index];
    if (!payloadItem) return;
    return (
      <span key={payloadItem.value + index.toString()} style={{ display: "block" }}>
        <i className="qes-dashboard__color-rect" style={{ backgroundColor: payloadItem.color }} />
        {payloadItem.value}
      </span>
    )
  });
  return legendItems;
}

export const decolorLegendText = (value: string): JSX.Element => {
  return <span style={{ color: "#6e6e8b" }}>{value}</span>;
};

export const getTrendingArrow = (enumValue: EcdTrendingArrowEnum, colorsContext: EcdColorsContextValues): JSX.Element => {
  switch (enumValue) {
    case EcdTrendingArrowEnum.BlueDown:
      return <QesIcon iconName={QesIcons.ArrowNarrowDown} description="Decrease" className="align-text-top ms-2" color={colorsContext.arrowPositive} />;
    case EcdTrendingArrowEnum.BlueNeutral: // shouldn't be possible
      return <QesIcon iconName={QesIcons.ArrowsHorizontal} description="No change" className="align-text-top ms-2" color={colorsContext.arrowPositive} />;
    case EcdTrendingArrowEnum.BlueUp:
      return <QesIcon iconName={QesIcons.ArrowNarrowUp} description="Increase" className="align-text-top ms-2" color={colorsContext.arrowPositive} />;
    case EcdTrendingArrowEnum.NeutralDown:
      return <QesIcon iconName={QesIcons.ArrowNarrowDown} description="Decrease" className="align-text-top ms-2" />;
    case EcdTrendingArrowEnum.NeutralNeutral:
      return <QesIcon iconName={QesIcons.ArrowsHorizontal} description="No change" className="align-text-top ms-2" />;
    case EcdTrendingArrowEnum.NeutralUp:
      return <QesIcon iconName={QesIcons.ArrowNarrowUp} description="Increase" className="align-text-top ms-2" />;
    case EcdTrendingArrowEnum.RedDown:
      return <QesIcon iconName={QesIcons.ArrowNarrowDown} description="Decrease" className="align-text-top ms-2" color={colorsContext.arrowNegative} />;
    case EcdTrendingArrowEnum.RedNeutral: // shouldn't be possible
      return <QesIcon iconName={QesIcons.ArrowsHorizontal} description="No change" className="align-text-top ms-2" color={colorsContext.arrowNegative} />;
    case EcdTrendingArrowEnum.RedUp:
      return <QesIcon iconName={QesIcons.ArrowNarrowUp} description="Increase" className="align-text-top ms-2" color={colorsContext.arrowNegative} />;
    case EcdTrendingArrowEnum.Unknown:
      return <span className="ms-2" />;
  }
}

export const getPercentChange = (initialValue: number, finalValue: number): number => {
  if (initialValue == null || initialValue == 0)
    return null;

  const percentChange = ((finalValue - initialValue) / Math.abs(initialValue)) * 100;
  return Math.round(percentChange * 10) / 10;
}

export const filterToTimeFrame = <T,>(data: T[], timeFrame: TimeFrameOptions, dataPointsPerMonth = 1): T[] => {
  switch (timeFrame) {
    case TimeFrameOptions.Three:
      return data.slice(-3 * dataPointsPerMonth);
    case TimeFrameOptions.Six:
      return data.slice(-6 * dataPointsPerMonth);
    case TimeFrameOptions.Twelve:
      return data.slice(-12 * dataPointsPerMonth);
    case TimeFrameOptions.Fifteen:
      return data.slice(-15 * dataPointsPerMonth);
  }
}
